import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ProcessFlowMapper = ({ processStages, onUpdateStages }) => {
  const [editingStage, setEditingStage] = useState(null);

  const riskLevelOptions = [
    { value: 'low', label: 'Rendah' },
    { value: 'medium', label: 'Sedang' },
    { value: 'high', label: 'Tinggi' },
    { value: 'critical', label: 'Kritis' }
  ];

  const getRiskColor = (level) => {
    const colors = {
      low: 'text-success',
      medium: 'text-warning',
      high: 'text-error',
      critical: 'text-destructive'
    };
    return colors?.[level] || 'text-muted-foreground';
  };

  const getRiskBgColor = (level) => {
    const colors = {
      low: 'bg-success/10',
      medium: 'bg-warning/10',
      high: 'bg-error/10',
      critical: 'bg-destructive/10'
    };
    return colors?.[level] || 'bg-muted';
  };

  const handleAddStage = () => {
    const newStage = {
      id: Date.now(),
      name: '',
      duration: '',
      bottleneck: false,
      riskLevel: 'low',
      notes: ''
    };
    onUpdateStages([...processStages, newStage]);
    setEditingStage(newStage?.id);
  };

  const handleUpdateStage = (id, field, value) => {
    const updated = processStages?.map(stage =>
      stage?.id === id ? { ...stage, [field]: value } : stage
    );
    onUpdateStages(updated);
  };

  const handleDeleteStage = (id) => {
    onUpdateStages(processStages?.filter(stage => stage?.id !== id));
  };

  return (
    <div className="card space-y-6">
      <div className="flex items-center justify-between pb-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
            <Icon name="GitBranch" size={24} color="var(--color-secondary)" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Pemetaan Alur Proses Produksi</h2>
            <p className="text-sm text-muted-foreground">Identifikasi tahapan dan bottleneck operasional</p>
          </div>
        </div>
        <Button
          variant="outline"
          iconName="Plus"
          iconPosition="left"
          onClick={handleAddStage}
        >
          Tambah Tahap
        </Button>
      </div>
      <div className="space-y-4">
        {processStages?.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Icon name="Workflow" size={48} className="mx-auto mb-4 opacity-50" />
            <p>Belum ada tahapan proses. Klik "Tambah Tahap" untuk memulai.</p>
          </div>
        ) : (
          processStages?.map((stage, index) => (
            <div key={stage?.id} className="relative">
              <div className={`p-4 md:p-6 rounded-lg border-2 transition-all ${
                editingStage === stage?.id ? 'border-primary bg-primary/5' : 'border-border bg-card'
              }`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <span className="text-sm font-bold text-primary data-text">{index + 1}</span>
                    </div>
                    {editingStage === stage?.id ? (
                      <Input
                        placeholder="Nama tahapan (contoh: Penerimaan Susu)"
                        value={stage?.name}
                        onChange={(e) => handleUpdateStage(stage?.id, 'name', e?.target?.value)}
                        className="flex-1"
                      />
                    ) : (
                      <div>
                        <h3 className="font-semibold">{stage?.name || 'Tahapan Baru'}</h3>
                        {stage?.duration && (
                          <p className="text-sm text-muted-foreground">Durasi: {stage?.duration} menit</p>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      iconName={editingStage === stage?.id ? "Check" : "Edit"}
                      onClick={() => setEditingStage(editingStage === stage?.id ? null : stage?.id)}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      iconName="Trash2"
                      onClick={() => handleDeleteStage(stage?.id)}
                    />
                  </div>
                </div>

                {editingStage === stage?.id && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <Input
                      label="Durasi (Menit)"
                      type="number"
                      placeholder="Contoh: 30"
                      value={stage?.duration}
                      onChange={(e) => handleUpdateStage(stage?.id, 'duration', e?.target?.value)}
                    />

                    <Select
                      label="Tingkat Risiko"
                      options={riskLevelOptions}
                      value={stage?.riskLevel}
                      onChange={(value) => handleUpdateStage(stage?.id, 'riskLevel', value)}
                    />

                    <div className="md:col-span-2">
                      <Input
                        label="Catatan Bottleneck"
                        placeholder="Jelaskan kendala atau hambatan pada tahap ini"
                        value={stage?.notes}
                        onChange={(e) => handleUpdateStage(stage?.id, 'notes', e?.target?.value)}
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={stage?.bottleneck}
                          onChange={(e) => handleUpdateStage(stage?.id, 'bottleneck', e?.target?.checked)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                        />
                        <span className="text-sm font-medium">Tandai sebagai bottleneck kritis</span>
                      </label>
                    </div>
                  </div>
                )}

                {editingStage !== stage?.id && stage?.name && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRiskBgColor(stage?.riskLevel)} ${getRiskColor(stage?.riskLevel)}`}>
                      Risiko: {riskLevelOptions?.find(opt => opt?.value === stage?.riskLevel)?.label}
                    </span>
                    {stage?.bottleneck && (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-destructive/10 text-destructive flex items-center gap-1">
                        <Icon name="AlertTriangle" size={14} />
                        Bottleneck
                      </span>
                    )}
                  </div>
                )}
              </div>

              {index < processStages?.length - 1 && (
                <div className="flex justify-center py-2">
                  <Icon name="ArrowDown" size={24} color="var(--color-muted-foreground)" />
                </div>
              )}
            </div>
          ))
        )}
      </div>
      {processStages?.length > 0 && (
        <div className="mt-6 p-4 rounded-lg bg-muted">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Total Tahapan</p>
              <p className="text-2xl font-bold data-text">{processStages?.length}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Bottleneck Teridentifikasi</p>
              <p className="text-2xl font-bold text-destructive data-text">
                {processStages?.filter(s => s?.bottleneck)?.length}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Total Durasi</p>
              <p className="text-2xl font-bold data-text">
                {processStages?.reduce((sum, s) => sum + (parseFloat(s?.duration) || 0), 0)} min
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Risiko Tinggi/Kritis</p>
              <p className="text-2xl font-bold text-error data-text">
                {processStages?.filter(s => s?.riskLevel === 'high' || s?.riskLevel === 'critical')?.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessFlowMapper;